<template>
  <div>
    <PageListTemplate :title="title" :subtitle="subtitle" :add="add">
      <template #actions>
        <!-- <AreaUnitWidget /> -->

        <v-btn
          @click.prevent="showSearch = true"
          class="rounded-lg"
          background-color="#ffffff"
          color="#cccccc"
          outlined
        >
          <v-icon color="secondary darken-1" size="16">
            mdi-map-search-outline
          </v-icon>

          <div
            class="secondary--text text--darken-1 text-body-2 text-none ml-1"
          >
            Mapa
          </div>
        </v-btn>
      </template>

      <template #default>
        <div v-if="!smAndDown" class="pa-3 mt-4 card-item">
          <FarmSearchWidget
            @change="(v) => filter(v)"
            :states="states"
            :cities="cities"
          />
        </div>

        <DataTableTemplate
          class="mt-4"
          no-data-text="Nenhuma área cadastrada"
          :items="filteredItems"
          :height="tableHeight"
          :loading="isLoading"
          :headers="headers"
        >
          <template v-slot:item.ref="{ item }">
            <div class="primary--text text-body-2 font-weight-medium">
              {{ item.ref || "" }}
            </div>
          </template>

          <template v-slot:item.nome="{ item }">
            <v-sheet
              class="secondary--text text--darken-2 text-body-2 font-weight-medium text-ellipsis"
              color="transparent"
              :title="item.nome"
              width="280px"
            >
              {{ item.nome || "" }}
            </v-sheet>
          </template>

          <template v-slot:item.valor_total="{ item }">
            <div
              v-if="item.valor_total"
              class="secondary--text text-body-2 text-ellipsis"
            >
              {{ formatPrice(item.valor_total) }}
            </div>
          </template>

          <template v-slot:item.city="{ item }">
            <div v-if="item.city?.name" class="d-flex align-center">
              <v-icon class="mr-1" color="secondary" size="16">
                mdi-map-marker-outline
              </v-icon>
              <div class="secondary--text">
                {{ `${item.city.name}, ${item.state.uf}` }}
              </div>
            </div>
          </template>

          <template v-slot:item.area_total="{ item }">
            <div class="position-relative">
              <v-tooltip v-if="!item.sistema_metrico && item.area_total" top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                    class="alert-inconsistency"
                    color="#fe9a00"
                    size="16"
                  >
                    mdi-alert-circle-outline
                  </v-icon>
                </template>

                <span>Unidade de área não cadastrada</span>
              </v-tooltip>

              <div class="secondary--text">
                {{ formatArea(item.sistema_metrico, item.area_total) }}
              </div>
            </div>
          </template>

          <template v-slot:item.area_aberta="{ item }">
            <div class="position-relative">
              <v-tooltip v-if="!item.sistema_metrico && item.area_aberta" top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                    class="alert-inconsistency"
                    color="#fe9a00"
                    size="16"
                  >
                    mdi-alert-circle-outline
                  </v-icon>
                </template>

                <span>Unidade de área não cadastrada</span>
              </v-tooltip>

              <div class="secondary--text">
                {{ formatArea(item.sistema_metrico, item.area_aberta) }}
              </div>
            </div>
          </template>

          <template v-slot:item.created_at="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" class="d-inline secondary--text">
                  {{ formatCreatedAt(item.created_at) }}
                </div>
              </template>

              <span>{{ formatCreatedAt(item.created_at, true) }}</span>
            </v-tooltip>
          </template>

          <template v-slot:item.cultura_desenv="{ item }">
            <v-chip
              v-if="formatPurpose(item.cultura_desenv)"
              class="chip-control"
              :style="colorPurposeById(item.cultura_desenv)"
            >
              {{ formatPurpose(item.cultura_desenv) }}
            </v-chip>
          </template>

          <template v-slot:item.acoes="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  @click="pin(item)"
                  class="ml-1"
                  :color="item.is_pinned ? 'grey' : 'red'"
                  small
                  icon
                >
                  <v-icon size="18">
                    {{ item.is_pinned ? "mdi-pin-off" : "mdi-pin" }}
                  </v-icon>
                </v-btn>
              </template>

              <span>{{
                item.is_pinned ? "Remover do topo" : "Fixar no topo"
              }}</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  :to="`/portfolio/${item.id}/ver`"
                  target="_blank"
                  color="green"
                  small
                  icon
                >
                  <v-icon size="20"> mdi-eye-outline </v-icon>
                </v-btn>
              </template>

              <span>
                Exibe todo o conteúdo que um cliente poderia acessar com
                permissões máximas. Não representa um link enviado.
              </span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  @click="toggleCreateLink(item)"
                  class="ml-1"
                  color="blue"
                  small
                  icon
                >
                  <v-icon size="20"> mdi-link </v-icon>
                </v-btn>
              </template>

              <span>Gerar link</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  class="ml-1"
                  color="#222222"
                  :to="`/portfolio/${item.id}`"
                  small
                  icon
                >
                  <v-icon size="16"> mdi-pencil-outline </v-icon>
                </v-btn>
              </template>

              <span>Editar</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-if="$can('remove_fazenda')"
                  v-bind="attrs"
                  v-on="on"
                  @click="toggleDelete(item)"
                  color="error"
                  small
                  icon
                >
                  <v-icon size="20"> mdi-delete-outline </v-icon>
                </v-btn>
              </template>

              <span>Excluir</span>
            </v-tooltip>
          </template>
        </DataTableTemplate>
      </template>
    </PageListTemplate>

    <ModalSearch :show="showSearch" @close="showSearch = false" />

    <v-dialog
      v-if="item?.id && dialogCreateLink"
      v-model="dialogCreateLink"
      max-width="600"
      persistent
      scrollable
    >
      <FarmCreateLinkDialog
        @close="toggleCreateLink"
        @success="(v) => toggleCreatedLink(v)"
        :item="item"
      />
    </v-dialog>

    <v-dialog
      v-if="linkCreated && dialogCreatedLink"
      v-model="dialogCreatedLink"
      max-width="600"
      persistent
    >
      <FarmCreatedLinkDialog @close="toggleCreatedLink" :link="linkCreated" />
    </v-dialog>

    <v-dialog
      v-if="item?.id && dialogDelete"
      v-model="dialogDelete"
      max-width="500"
      persistent
    >
      <DeleteItemDialog
        @confirm="onDelete"
        @close="toggleDelete"
        title="Apagar área"
        :name="item.nome"
      />
    </v-dialog>
  </div>
</template>

<script>
import AreaUnitWidget from "@/components/widgets/AreaUnit.vue";
import PageListTemplate from "@/components/templates/PageList.vue";
import FarmSearchWidget from "@/components/widgets/FarmSearch.vue";
import DataTableTemplate from "@/components/templates/DataTable.vue";
import FarmCreateLinkDialog from "@/components/dialogs/FarmCreateLink.vue";
import FarmCreatedLinkDialog from "@/components/dialogs/FarmCreatedLink.vue";
import DeleteItemDialog from "@/components/dialogs/DeleteItem.vue";
import ModalSearch from "@/components/ModalSearch.vue";
import variables from "@/utils/variables";
import formatPurpose from "@/utils/formatPurpose";
import formatArea from "@/utils/formatArea";
import formatCreatedAt from "@/utils/formatCreatedAt";
import colorPurposeById from "@/utils/colorPurposeById";
import { mapActions } from "vuex";

export default {
  components: {
    AreaUnitWidget,
    PageListTemplate,
    FarmSearchWidget,
    DataTableTemplate,
    FarmCreateLinkDialog,
    FarmCreatedLinkDialog,
    DeleteItemDialog,
    ModalSearch,
  },
  data: () => ({
    showSearch: false,
    page: 1,
    isLoading: true,
    title: "Portfólio",
    subtitle: "Gerencie suas áreas cadastradas",
    items: [],
    states: [],
    cities: [],
    filters: {},
    item: null,
    dialogCreateLink: false,
    linkCreated: null,
    dialogCreatedLink: false,
    dialogDelete: false,
    isLoadingDelete: false,
    purpose: variables.purpose,
  }),
  computed: {
    tableHeight() {
      const name = this.$vuetify.breakpoint.name;

      const offset = ["lg", "md"].includes(name)
        ? 420
        : name === "sm"
        ? 235
        : name === "xs"
        ? 210
        : 350;

      return `calc(100dvh - ${offset}px)`;
    },
    add() {
      if (!this.$can("add_fazenda")) return;

      return {
        link: "/portfolio/adicionar",
        text: "Nova Área",
      };
    },
    headers() {
      const lgAndDown = this.$vuetify.breakpoint.lgAndDown;

      const headers = [
        {
          text: "Código",
          align: "start",
          value: "ref",
          width: lgAndDown ? 100 : 80,
        },
        {
          text: "Nome",
          align: "start",
          value: "nome",
          width: 250,
        },
        {
          text: "Valor",
          align: "start",
          value: "valor_total",
          width: 120,
        },
        {
          text: "Área total",
          align: "start",
          value: "area_total",
          width: lgAndDown ? 120 : 120,
        },
        {
          text: "Área aberta",
          align: "start",
          value: "area_aberta",
          width: lgAndDown ? 130 : 120,
        },
        {
          text: "Finalidade",
          align: "start",
          value: "cultura_desenv",
          width: 120,
        },
        {
          text: "Município",
          align: "start",
          value: "city",
          width: 200,
        },
        {
          text: "Ações",
          align: "center",
          sortable: false,
          value: "acoes",
          width: lgAndDown ? 200 : 170,
        },
      ];

      return headers;
    },
    sortedItems() {
      return this.items.sort((a, b) => {
        return b.is_pinned - a.is_pinned;
      });
    },
    filteredItems() {
      return this.sortedItems.filter((item) => {
        if (this.filters.text) {
          const t = this.filters.text.toLowerCase();
          if (![item.ref, item.nome].join(" ").toLowerCase().includes(t))
            return false;
        }

        if (
          this.filters.minAreaTotal > 0 &&
          item.area_total < this.filters.minAreaTotal
        )
          return false;

        if (
          this.filters.maxAreaTotal > 0 &&
          item.area_total > this.filters.maxAreaTotal
        )
          return false;

        if (
          this.filters.minAreaAberta > 0 &&
          item.area_aberta < this.filters.minAreaAberta
        )
          return false;

        if (
          this.filters.maxAreaAberta > 0 &&
          item.area_aberta > this.filters.maxAreaAberta
        )
          return false;

        if (
          this.filters.cultura &&
          item.cultura_desenv !== this.filters.cultura
        )
          return false;

        if (this.filters.tipo && item.tipo_imovel !== this.filters.tipo)
          return false;

        if (
          this.filters.states &&
          this.filters.states.length > 0 &&
          !this.filters.states.includes(item.state_id)
        )
          return false;

        if (
          this.filters.cities &&
          this.filters.cities.length > 0 &&
          !this.filters.cities.includes(item.city_id)
        )
          return false;

        return true;
      });
    },
    filteredCities() {
      if (!this.states.length) return [];
    },
    smAndDown() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    xsOnly() {
      return this.$vuetify.breakpoint.xsOnly;
    },
  },
  methods: {
    ...mapActions("properties", [
      "listProperties",
      "destroyProperty",
      "pinProperty",
    ]),
    ...mapActions("states", ["listStatesWithFarm"]),
    ...mapActions("cities", ["listCitiesByStateWithFarm"]),
    arrayFilter(a, b) {
      return [...a, ...b].filter(
        (item, _, arr) => arr.filter((v) => v === item).length === 1,
      );
    },
    formatPrice(v) {
      const number = Number(v);
      if (isNaN(number)) return;

      return (
        "R$ " +
        number.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    },
    async filter(v) {
      const hadStates = this.filters.states && this.filters.states.length > 0;

      if (hadStates && !v.states.length) this.cities = [];

      if (
        (hadStates && this.arrayFilter(this.filters.states, v.states).length) ||
        v.states.length > 0
      ) {
        const cities = (
          await Promise.all(
            v.states.map((el) => this.listCitiesByStateWithFarm(el)),
          )
        ).flat();

        const usedStates = new Set();
        const items = [];

        cities.forEach(({ id, name, state_id }) => {
          const state = this.states.find(({ value }) => value === state_id);

          if (!usedStates.has(state_id)) {
            usedStates.add(state_id);
            items.push({ header: state.text });
          }

          items.push({
            text: name,
            value: id,
          });
        });

        this.cities = items;
      }

      this.filters = v;
    },
    async pin(item) {
      const r = await this.pinProperty(item.id);

      if (r) {
        this.$eventHub.$emit("snackBar", {
          icon: "mdi-pin",
          message: item.is_pinned
            ? "Área removida do topo com sucesso."
            : "Área fixada no topo com sucesso.",
          type: "success",
        });

        this.onLoad(false);
      } else {
        this.$eventHub.$emit("snackBar", {
          icon: "mdi-map-marker-alert",
          message: "Não foi possível fixar a área. Tente novamente.",
          type: "error",
        });
      }
    },
    async onLoad(loading = true, page = 1, itemsPerPage = 10000) {
      this.page = page;
      if (loading) this.isLoading = true;

      let filters = {
        page: page,
        itemsPerPage,
      };

      const res = await this.listProperties(filters);
      this.items = res.data.map((item) => ({
        ...item,
        ref: this.formatRef(item.tipo_imovel, item.id),
      }));

      if (loading) this.isLoading = false;
    },
    toggleCreateLink(item = null) {
      if (this.dialogCreateLink) {
        this.dialogCreateLink = false;
        this.item = null;
      } else {
        this.item = item;
        this.dialogCreateLink = true;
      }
    },
    toggleCreatedLink(link = null) {
      if (this.dialogCreatedLink) {
        this.dialogCreatedLink = false;
        this.linkCreated = null;
      } else {
        this.linkCreated = link;
        this.dialogCreatedLink = true;
      }
    },
    toggleDelete(item = null) {
      if (this.dialogDelete) {
        this.dialogDelete = false;
        this.item = null;
      } else {
        this.item = item;
        this.dialogDelete = true;
      }
    },
    async onDelete() {
      this.isLoadingDelete = true;

      const r = await this.destroyProperty(this.item.id);

      if (r) {
        this.$eventHub.$emit("snackBar", {
          icon: "mdi-map-marker-remove",
          message: "Área excluída com sucesso.",
          type: "success",
        });

        this.item = null;
        this.dialogDelete = false;
        this.onLoad();
      } else {
        this.$eventHub.$emit("snackBar", {
          icon: "mdi-map-marker-alert",
          message: "Não foi possível excluir a área. Tente novamente.",
          type: "error",
        });
      }

      this.isLoadingDelete = false;
    },
    formatRef(type, id) {
      const types = {
        1: "FA",
        2: "SI",
        3: "CH",
        4: "AM",
        5: "PU",
        6: "AP",
      };

      return types[type] + id || id;
    },
    formatPurpose,
    formatArea,
    formatCreatedAt,
    colorPurposeById,
  },
  async mounted() {
    this.onLoad();

    this.states = await this.listStatesWithFarm();
  },
};
</script>
